import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiOutlinePlus, HiOutlineArrowPath, HiOutlineCheckCircle, HiOutlineExclamationTriangle } from 'react-icons/hi2';
import { workPermitService, plantService, lineService } from '../services';
import PageLoading from '../components/common/PageLoading';
import './WorkPermitList.css';

const statusColors = {
  Draft: 'badge-grey',
  'Under Review': 'badge-indigo',
  Issued: 'badge-green',
  Active: 'badge-green',
  'Work in Progress': 'badge-blue',
  Completed: 'badge-teal',
  Closed: 'badge-neutral',
  Cancelled: 'badge-red',
  Suspended: 'badge-red',
};

const WorkPermitList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const initialStatus = query.get('status') || '';

  const [permits, setPermits] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: initialStatus,
    search: '',
    startDate: '',
    endDate: '',
    plantId: '',
    lineId: '',
  });
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load master data if not loaded
      if (plants.length === 0) {
        const [plantData, lineData] = await Promise.all([
          plantService.getAllPlants(),
          lineService.getAllLines(),
        ]);
        setPlants(plantData || []);
        setLines(lineData || []);

        // Pre-select plant if only 1 exists
        if (plantData?.length === 1) {
          setFilters(prev => ({ ...prev, plantId: plantData[0].plantId.toString() }));
        }
      }

      const response = await workPermitService.getWorkPermits({
        status: filters.status || undefined,
        startDate: filters.startDate ? new Date(filters.startDate) : undefined,
        endDate: filters.endDate ? new Date(filters.endDate) : undefined,
      });
      setPermits(response || []);

      const expiringData = await workPermitService.getExpiringPermits(4);
      setExpiring(expiringData || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load work permits.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPermits = useMemo(() => {
    return permits.filter((p) => {
      const matchesStatus = !filters.status || p.status === filters.status;
      const matchesSearch =
        !filters.search ||
        p.permitNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
        p.workOrderNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
        p.equipmentName?.toLowerCase().includes(filters.search.toLowerCase());
      
      const matchesPlant = !filters.plantId || 
        p.plantId?.toString() === filters.plantId || 
        (lines.find(l => l.lineId === p.lineId)?.plantId?.toString() === filters.plantId);
      
      const matchesLine = !filters.lineId || p.lineId?.toString() === filters.lineId;

      return matchesStatus && matchesSearch && matchesPlant && matchesLine;
    });
  }, [permits, filters, lines]);

  const summary = useMemo(() => {
    const total = permits.length;
    const active = permits.filter((p) => ['Active', 'Issued', 'Work in Progress'].includes(p.status)).length;
    const expiringSoon = expiring.length;
    const closed = permits.filter((p) => ['Closed', 'Cancelled'].includes(p.status)).length;
    return { total, active, expiringSoon, closed };
  }, [permits, expiring]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'plantId') {
        next.lineId = '';
        if (value) {
            localStorage.setItem('defaultPlantId', value);
        }
      }
      return next;
    });
  };

  const handleApply = () => loadData();

  return (
    <div className="pagecontainer work-permit-list-page">
      <div className="page-header">
        <div>
          <h1>Work Permits</h1>
          <p>Track, issue, and monitor all permits with expiring alerts.</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={loadData}>
            <HiOutlineArrowPath className="btn-icon" />
            Refresh
          </button>
          <button className="btn-primary" onClick={() => navigate('/home/work-permits/new')}>
            <HiOutlinePlus className="btn-icon" />
            New Permit
          </button>
        </div>
      </div>

      <div className="summary-grid">
        <div className="summary-card">
          <span className="label">Total Permits</span>
          <span className="value">{summary.total}</span>
        </div>
        <div className="summary-card">
          <span className="label">Active / WIP</span>
          <span className="value accent">{summary.active}</span>
        </div>
        <div className="summary-card">
          <span className="label">Expiring Soon</span>
          <span className="value warning">{summary.expiringSoon}</span>
        </div>
        <div className="summary-card">
          <span className="label">Closed / Cancelled</span>
          <span className="value">{summary.closed}</span>
        </div>
      </div>

      <div className="filters-card">
        <div className="form-row">
          <div className="form-group">
            <label>Plant</label>
            <select name="plantId" value={filters.plantId} onChange={handleChange}>
              <option value="">All Plants</option>
              {plants.map(p => (
                <option key={p.plantId} value={p.plantId}>{p.plantName}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Line</label>
            <select 
                name="lineId" 
                value={filters.lineId} 
                onChange={handleChange}
                disabled={!filters.plantId}
            >
              <option value="">All Lines</option>
              {lines.filter(l => !filters.plantId || l.plantId?.toString() === filters.plantId).map(l => (
                <option key={l.lineId} value={l.lineId}>{l.lineName}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select name="status" value={filters.status} onChange={handleChange}>
              <option value="">All</option>
              <option value="Draft">Draft</option>
              <option value="Under Review">Under Review</option>
              <option value="Issued">Issued</option>
              <option value="Active">Active</option>
              <option value="Work in Progress">Work in Progress</option>
              <option value="Closed">Closed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="form-group">
            <label>Search</label>
            <input
              type="text"
              name="search"
              placeholder="Permit #, WO #, Equipment"
              value={filters.search}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Start Date</label>
            <input type="date" name="startDate" value={filters.startDate} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>End Date</label>
            <input type="date" name="endDate" value={filters.endDate} onChange={handleChange} />
          </div>
          <div className="form-group align-end">
            <button className="btn-primary" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <PageLoading message="Loading work permits…" />
      ) : (
        <div className="table-card">
          <div className="table-head">
            <span>Permits</span>
            <span className="hint">Click a row to view or edit</span>
          </div>
          <div className="table-responsive">
            <table className="wp-table">
              <thead>
                <tr>
                  <th>Permit #</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Equipment</th>
                  <th>Valid</th>
                  <th>Requested By</th>
                  <th>Issued By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPermits.length === 0 && (
                  <tr>
                    <td colSpan="8" className="empty-state">
                      No permits found for selected filters.
                    </td>
                  </tr>
                )}
                {filteredPermits.map((p) => (
                  <tr key={p.permitId} onClick={() => navigate(`/home/work-permits/${p.permitId}`)}>
                    <td>{p.permitNumber}</td>
                    <td>{p.permitTypeName || '-'}</td>
                    <td>
                      <span className={`status-badge ${statusColors[p.status] || 'badge-neutral'}`}>{p.status}</span>
                      {expiring.some((e) => e.permitId === p.permitId) && (
                        <span className="status-badge badge-amber">
                          <HiOutlineExclamationTriangle /> Expiring
                        </span>
                      )}
                    </td>
                    <td>
                      <div>{p.equipmentName || '-'}</div>
                      <small className="muted">{p.lineName || p.plantName}</small>
                    </td>
                    <td>
                      <div>{p.validFrom ? new Date(p.validFrom).toLocaleDateString() : '-'}</div>
                      <small className="muted">to {p.validTo ? new Date(p.validTo).toLocaleDateString() : '-'}</small>
                    </td>
                    <td>{p.requesterName || '-'}</td>
                    <td>{p.issuerName || '-'}</td>
                    <td>
                      <div className="row-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="link-btn" onClick={() => navigate(`/home/work-permits/${p.permitId}`)}>
                          Edit
                        </button>
                        <button className="link-btn subtle" onClick={() => navigate(`/home/work-order/${p.workOrderId || ''}`)}>
                          WO
                        </button>
                        {p.status === 'Active' && (
                          <button className="link-btn danger">
                            <HiOutlineCheckCircle className="btn-icon" />
                            Close
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkPermitList;

